import React, { useState, useEffect } from 'react';
import { logger } from '../utils/logger';

interface HealthStatus {
  video: { ok: boolean; reason?: string; videoEnabled?: boolean };
  notion: { ok: boolean; notionReady?: boolean; missing?: any };
  quota: { ok: boolean; quota?: number; used?: number };
  timestamp: string;
}

export const ServerStatus: React.FC = () => {
  const [status, setStatus] = useState<HealthStatus | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if in development mode or if user has toggled visibility
    const isDev = import.meta.env.DEV || localStorage.getItem('showServerStatus') === 'true';
    setIsVisible(isDev);
    
    if (isDev) {
      checkHealth();
      // Refresh every 30 seconds
      const interval = setInterval(checkHealth, 30000);
      return () => clearInterval(interval);
    }
  }, []);

  const checkHealth = async () => {
    setLoading(true);
    const health: HealthStatus = {
      video: { ok: false },
      notion: { ok: false },
      quota: { ok: false },
      timestamp: new Date().toISOString()
    };

    // Check video health
    try {
      const videoRes = await fetch('/api/video/health');
      if (videoRes.ok) {
        health.video = await videoRes.json();
      } else {
        health.video = { ok: false, reason: `HTTP ${videoRes.status}` };
      }
    } catch (error) {
      health.video = { ok: false, reason: 'Network error' };
      logger.error('VIDEO_HEALTH_CHECK_FAILED', { error });
    }

    // Check Notion health
    try {
      const notionRes = await fetch('/api/notion/health');
      if (notionRes.ok) {
        health.notion = await notionRes.json();
      } else {
        health.notion = { ok: false, reason: `HTTP ${notionRes.status}` };
      }
    } catch (error) {
      health.notion = { ok: false, reason: 'Network error' };
      logger.error('NOTION_HEALTH_CHECK_FAILED', { error });
    }

    // Check quota
    try {
      const quotaRes = await fetch('/api/quota');
      if (quotaRes.ok) {
        health.quota = await quotaRes.json();
      } else {
        health.quota = { ok: false, reason: `HTTP ${quotaRes.status}` };
      }
    } catch (error) {
      health.quota = { ok: false, reason: 'Network error' };
    }

    setStatus(health);
    setLoading(false);
    logger.info('SERVER_HEALTH_CHECK', health);
  };

  const toggleVisibility = () => {
    const newState = !isVisible;
    setIsVisible(newState);
    localStorage.setItem('showServerStatus', newState.toString());
    if (newState && !status) {
      checkHealth();
    }
  };

  // Developer toggle button (always visible)
  const toggleButton = (
    <button
      onClick={toggleVisibility}
      className="fixed bottom-4 left-4 z-50 p-2 bg-slate-800 hover:bg-slate-700 rounded-lg shadow-lg border border-slate-600 transition-all"
      title="Toggle Server Status"
    >
      <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
          d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
      </svg>
    </button>
  );

  if (!isVisible) {
    return toggleButton;
  }

  const getStatusColor = (ok: boolean) => ok ? 'text-green-400' : 'text-red-400';
  const getStatusIcon = (ok: boolean) => ok ? '✅' : '❌';

  return (
    <>
      {toggleButton}
      <div className="fixed bottom-4 left-16 z-40 max-w-md">
        <div className="bg-slate-900 border border-slate-700 rounded-lg shadow-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-300">Server Status</h3>
            <button
              onClick={checkHealth}
              disabled={loading}
              className="p-1 hover:bg-slate-800 rounded transition-colors"
              title="Refresh"
            >
              <svg className={`w-4 h-4 text-slate-400 ${loading ? 'animate-spin' : ''}`} 
                fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          </div>

          {loading && !status ? (
            <div className="text-slate-400 text-xs">Checking...</div>
          ) : status ? (
            <div className="space-y-2 text-xs">
              {/* Video API Status */}
              <div className="flex items-center justify-between">
                <span className="text-slate-400">Video API:</span>
                <div className="flex items-center gap-2">
                  <span className={getStatusColor(status.video.ok)}>
                    {getStatusIcon(status.video.ok)}
                  </span>
                  {status.video.reason && (
                    <span className="text-red-400 text-xs">({status.video.reason})</span>
                  )}
                </div>
              </div>

              {/* Notion API Status */}
              <div className="flex items-center justify-between">
                <span className="text-slate-400">Notion API:</span>
                <div className="flex items-center gap-2">
                  <span className={getStatusColor(status.notion.ok)}>
                    {getStatusIcon(status.notion.ok)}
                  </span>
                  {status.notion.missing && (
                    <span className="text-orange-400 text-xs">
                      Missing: {Object.entries(status.notion.missing)
                        .filter(([_, v]) => v)
                        .map(([k]) => k)
                        .join(', ')}
                    </span>
                  )}
                </div>
              </div>

              {/* Quota Status */}
              <div className="flex items-center justify-between">
                <span className="text-slate-400">Quota API:</span>
                <div className="flex items-center gap-2">
                  {status.quota.quota !== undefined ? (
                    <span className="text-blue-400">
                      {status.quota.used}/{status.quota.quota}
                    </span>
                  ) : (
                    <span className="text-slate-500">N/A</span>
                  )}
                </div>
              </div>

              {/* Last Check */}
              <div className="pt-2 mt-2 border-t border-slate-800">
                <span className="text-slate-500 text-xs">
                  Last check: {new Date(status.timestamp).toLocaleTimeString()}
                </span>
              </div>

              {/* Deployment Info */}
              <div className="pt-2 text-slate-500 text-xs">
                <div>Domain: {window.location.hostname}</div>
                <div>Functions: /api/* (Cloudflare Pages)</div>
              </div>
            </div>
          ) : (
            <div className="text-red-400 text-xs">Failed to check status</div>
          )}
        </div>
      </div>
    </>
  );
};